/**
 * Personal Stats Tab
 *
 * Shows personal progress, celebrates effort, encourages learning.
 * NO comparisons, NO shame, focus on "you then vs you now".
 *
 * See: /docs/STATS_PHILOSOPHY.md
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "@/db/database";
import { TrendingUp, Clock, Target, Calendar } from "lucide-react";
import { ActivityCalendar } from "@/components/stats/ActivityCalendar";
import { QuizSpecificStats } from "@/components/stats/QuizSpecificStats";

export function PersonalStatsTab() {
  // Load all user data
  const sessions = useLiveQuery(() => db.quiz_sessions.toArray());
  const knowledgeStates = useLiveQuery(() => db.knowledge_state.toArray());
  const attempts = useLiveQuery(() => db.quiz_attempts.toArray());

  // Calculate lifetime stats
  const lifetimeStats = calculateLifetimeStats(sessions, knowledgeStates, attempts);

  return (
    <Tabs defaultValue="overview" className="space-y-6">
      <TabsList className="grid w-full grid-cols-3">
        <TabsTrigger value="overview">Overview</TabsTrigger>
        <TabsTrigger value="activity">Activity</TabsTrigger>
        <TabsTrigger value="quizzes">By Quiz</TabsTrigger>
      </TabsList>

      {/* Overview Tab */}
      <TabsContent value="overview" className="space-y-6">
        {/* Lifetime Stats Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="Regions Learned"
            value={lifetimeStats.regionsLearned}
            subtitle="Areas you've practiced"
            icon={<Target className="w-4 h-4" />}
            trend={lifetimeStats.recentRegions > 0 ? `+${lifetimeStats.recentRegions} this week` : undefined}
          />

          <StatCard
            title="Time Invested"
            value={formatTime(lifetimeStats.totalTimeMs)}
            subtitle="Learning and reviewing"
            icon={<Clock className="w-4 h-4" />}
            trend={lifetimeStats.recentTimeMs > 0 ? `${formatTime(lifetimeStats.recentTimeMs)} this week` : undefined}
          />

          <StatCard
            title="Practice Sessions"
            value={lifetimeStats.totalSessions}
            subtitle="Quizzes completed"
            icon={<TrendingUp className="w-4 h-4" />}
            trend={lifetimeStats.recentSessions > 0 ? `${lifetimeStats.recentSessions} this week` : undefined}
          />

          <StatCard
            title="Questions Answered"
            value={lifetimeStats.totalQuestions}
            subtitle="Your effort counts!"
            icon={<Calendar className="w-4 h-4" />}
            trend={lifetimeStats.recentQuestions > 0 ? `${lifetimeStats.recentQuestions} this week` : undefined}
          />
        </div>

        {/* Gentle Streak Card (if active) */}
        {lifetimeStats.currentStreak > 0 && (
          <Card className="border border-primary/30 bg-primary/5 shadow-lg shadow-primary/10 backdrop-blur">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="text-4xl">🔥</div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold">
                    {lifetimeStats.currentStreak} Day Streak!
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    You're on a roll! Keep the momentum going.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Welcome Back Card (if inactive) */}
        {lifetimeStats.daysSinceLastSession > 1 && lifetimeStats.currentStreak === 0 && (
          <Card className="border border-border/70 bg-white/80 shadow-lg shadow-primary/10 backdrop-blur dark:bg-secondary/70">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="text-4xl">👋</div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold">Welcome Back!</h3>
                  <p className="text-sm text-muted-foreground">
                    Last session: {lifetimeStats.daysSinceLastSession} days ago. Ready to continue where you left off?
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Suggested Reviews */}
        {lifetimeStats.needsReview > 0 && (
          <Card className="border border-border/70 bg-white/80 shadow-lg shadow-primary/10 backdrop-blur dark:bg-secondary/70">
            <CardHeader>
              <CardTitle className="text-lg">Time for a Refresh?</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                {lifetimeStats.needsReview} {lifetimeStats.needsReview === 1 ? 'region' : 'regions'} {lifetimeStats.needsReview === 1 ? 'hasn\'t' : 'haven\'t'} been reviewed in 2+ weeks.
                A quick review helps keep your knowledge fresh!
              </p>
              <div className="text-sm text-muted-foreground">
                💡 Tip: Review sessions help solidify what you've learned. Don't worry about accuracy - mistakes help you learn!
              </div>
            </CardContent>
          </Card>
        )}
      </TabsContent>

      {/* Activity Tab */}
      <TabsContent value="activity" className="space-y-6">
        <ActivityCalendar sessions={sessions || []} />

        {/* Activity Summary */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card className="border border-border/70 bg-white/80 shadow-lg shadow-primary/10 backdrop-blur dark:bg-secondary/70">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">Most Active Day</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {getMostActiveDay(sessions || [])}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Day of the week you practice most
              </p>
            </CardContent>
          </Card>

          <Card className="border border-border/70 bg-white/80 shadow-lg shadow-primary/10 backdrop-blur dark:bg-secondary/70">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">Average Session</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {getAverageQuestions(sessions || [])}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Questions per session
              </p>
            </CardContent>
          </Card>

          <Card className="border border-border/70 bg-white/80 shadow-lg shadow-primary/10 backdrop-blur dark:bg-secondary/70">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">Best Week</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {getBestWeekSessions(sessions || [])}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Sessions in your most productive week
              </p>
            </CardContent>
          </Card>
        </div>
      </TabsContent>

      {/* By Quiz Tab */}
      <TabsContent value="quizzes" className="space-y-6">
        <QuizSpecificStats />
      </TabsContent>
    </Tabs>
  );
}

// ============================================================================
// Stat Card Component
// ============================================================================

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle: string;
  icon: React.ReactNode;
  trend?: string;
}

function StatCard({ title, value, subtitle, icon, trend }: StatCardProps) {
  return (
    <Card className="border border-border/70 bg-white/80 shadow-lg shadow-primary/10 backdrop-blur dark:bg-secondary/70">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <div className="text-muted-foreground">{icon}</div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
        {trend && (
          <p className="text-xs text-primary mt-2 font-medium">
            {trend}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Stats Calculation
// ============================================================================

interface LifetimeStats {
  regionsLearned: number;
  totalTimeMs: number;
  totalSessions: number;
  totalQuestions: number;
  recentRegions: number;
  recentTimeMs: number;
  recentSessions: number;
  recentQuestions: number;
  currentStreak: number;
  daysSinceLastSession: number;
  needsReview: number;
}

function calculateLifetimeStats(
  sessions: any[] | undefined,
  knowledgeStates: any[] | undefined,
  attempts: any[] | undefined
): LifetimeStats {
  if (!sessions || !knowledgeStates || !attempts) {
    return {
      regionsLearned: 0,
      totalTimeMs: 0,
      totalSessions: 0,
      totalQuestions: 0,
      recentRegions: 0,
      recentTimeMs: 0,
      recentSessions: 0,
      recentQuestions: 0,
      currentStreak: 0,
      daysSinceLastSession: 0,
      needsReview: 0,
    };
  }

  const now = Date.now();
  const oneWeekAgo = now - 7 * 24 * 60 * 60 * 1000;
  const twoWeeksAgo = now - 14 * 24 * 60 * 60 * 1000;

  // Total stats
  const regionsLearned = knowledgeStates.length;
  const totalTimeMs = sessions.reduce((sum, s) => sum + (s.totalTimeMs || 0), 0);
  const totalSessions = sessions.length;
  const totalQuestions = attempts.length;

  // Recent stats (last 7 days)
  const recentSessionsList = sessions.filter(s => new Date(s.completedAt).getTime() > oneWeekAgo);
  const recentSessions = recentSessionsList.length;
  const recentTimeMs = recentSessionsList.reduce((sum, s) => sum + (s.totalTimeMs || 0), 0);
  const recentQuestions = attempts.filter(a => new Date(a.attemptedAt).getTime() > oneWeekAgo).length;

  // Recent regions (practiced in last 7 days)
  const recentRegions = new Set(
    knowledgeStates.filter(k => new Date(k.lastUpdated).getTime() > oneWeekAgo).map(k => k.region)
  ).size;

  // Streak calculation
  const sortedSessions = [...sessions].sort((a, b) =>
    new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime()
  );

  let currentStreak = 0;
  let daysSinceLastSession = 0;

  if (sortedSessions.length > 0) {
    const lastSessionDate = new Date(sortedSessions[0].completedAt);
    daysSinceLastSession = Math.floor((now - lastSessionDate.getTime()) / (24 * 60 * 60 * 1000));

    // Calculate streak (consecutive days with sessions)
    if (daysSinceLastSession <= 1) {
      let currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0);

      for (const session of sortedSessions) {
        const sessionDate = new Date(session.completedAt);
        sessionDate.setHours(0, 0, 0, 0);

        const daysDiff = Math.floor((currentDate.getTime() - sessionDate.getTime()) / (24 * 60 * 60 * 1000));

        if (daysDiff <= 1) {
          if (sessionDate.getTime() === currentDate.getTime()) {
            currentStreak = Math.max(currentStreak, 1);
          } else {
            currentStreak++;
            currentDate = sessionDate;
          }
        } else {
          break;
        }
      }
    }
  }

  // Needs review (regions not practiced in 2+ weeks)
  const needsReview = knowledgeStates.filter(k =>
    new Date(k.lastUpdated).getTime() < twoWeeksAgo
  ).length;

  return {
    regionsLearned,
    totalTimeMs,
    totalSessions,
    totalQuestions,
    recentRegions,
    recentTimeMs,
    recentSessions,
    recentQuestions,
    currentStreak,
    daysSinceLastSession,
    needsReview,
  };
}

// ============================================================================
// Utilities
// ============================================================================

function formatTime(ms: number): string {
  const hours = Math.floor(ms / (1000 * 60 * 60));
  const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}

/**
 * Get most active day of the week
 */
function getMostActiveDay(sessions: any[]): string {
  if (sessions.length === 0) return "N/A";

  const dayCounts: Record<string, number> = {
    Sunday: 0,
    Monday: 0,
    Tuesday: 0,
    Wednesday: 0,
    Thursday: 0,
    Friday: 0,
    Saturday: 0,
  };

  const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"] as const;

  sessions.forEach((session) => {
    const date = new Date(session.completedAt);
    const dayIndex = date.getDay();
    const dayName = dayNames[dayIndex];
    if (dayName !== undefined) {
      const key = dayName as keyof typeof dayCounts;
      dayCounts[key] = (dayCounts[key] || 0) + 1;
    }
  });

  let maxDay = "N/A";
  let maxCount = 0;

  Object.entries(dayCounts).forEach(([day, count]) => {
    if (count > maxCount) {
      maxCount = count;
      maxDay = day;
    }
  });

  return maxDay;
}

/**
 * Get average questions per session
 */
function getAverageQuestions(sessions: any[]): string {
  if (sessions.length === 0) return "0";

  const total = sessions.reduce((sum, s) => sum + (s.totalQuestions || 0), 0);
  const avg = Math.round(total / sessions.length);

  return avg.toString();
}

/**
 * Get best week's session count
 */
function getBestWeekSessions(sessions: any[]): string {
  if (sessions.length === 0) return "0";

  // Group sessions by week
  const weekCounts = new Map<string, number>();

  sessions.forEach((session) => {
    const date = new Date(session.completedAt);
    const year = date.getFullYear();
    const week = getISOWeek(date);
    const key = `${year}-W${week}`;

    weekCounts.set(key, (weekCounts.get(key) || 0) + 1);
  });

  const maxCount = Math.max(...Array.from(weekCounts.values()));
  return maxCount.toString();
}

/**
 * Get ISO week number
 */
function getISOWeek(date: Date): number {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  const yearStart = new Date(d.getFullYear(), 0, 1);
  const weekNo = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return weekNo;
}
